/**
 * Format utility functions
 */

/**
 * Format duration in seconds to HH:MM:SS
 */
export const formatDuration = (seconds) => {
  if (!seconds) return '00:00:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  return [hours, minutes, secs]
    .map(val => val.toString().padStart(2, '0'))
    .join(':');
};

/**
 * Format seconds to hours (decimal)
 */
export const formatHours = (seconds) => {
  if (!seconds) return '0.00';
  return (seconds / 3600).toFixed(2);
};

/**
 * Format currency. Uses the ISO code (e.g. "USD 1,188.00") instead of
 * just the symbol ("$1,188.00") so cross-border payors don't misread
 * USD as CAD or ₱ as something other than the Philippine peso. Adds a
 * few characters to every render — worth it for the disambiguation.
 */
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
  }).format(amount || 0);
};

/**
 * Convert various date formats to Date object
 */
const toDate = (date) => {
  if (!date) return null;

  // Already a Date
  if (date instanceof Date) return date;

  // Firestore Timestamp with toDate method
  if (date.toDate && typeof date.toDate === 'function') {
    return date.toDate();
  }

  // Serialized Firestore Timestamp from Cloud Functions {_seconds, _nanoseconds}
  if (date._seconds !== undefined) {
    return new Date(date._seconds * 1000);
  }

  // Firestore Timestamp object {seconds, nanoseconds}
  if (date.seconds !== undefined) {
    return new Date(date.seconds * 1000);
  }

  // ISO string or timestamp number
  const d = new Date(date);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Format date
 */
export const formatDate = (date) => {
  if (!date) return '';

  const d = toDate(date);
  if (!d) return '';

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
};

/**
 * Format date and time
 */
export const formatDateTime = (date) => {
  if (!date) return '';

  const d = toDate(date);
  if (!d) return '';

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

/**
 * Get relative time (e.g., "2 days ago")
 */
export const getRelativeTime = (date) => {
  if (!date) return '';

  const d = toDate(date);
  if (!d) return '';

  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

/**
 * Check if date is overdue
 */
export const isOverdue = (dueDate) => {
  if (!dueDate) return false;
  const d = toDate(dueDate);
  if (!d) return false;
  return d < new Date();
};

/**
 * Calculate days until/since date
 */
export const daysDifference = (date) => {
  if (!date) return 0;
  const d = toDate(date);
  if (!d) return 0;
  const now = new Date();
  const diffMs = d - now;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};
